import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation } from "wouter";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { SITE_PHONE, LOGO_URL, services, industries } from "@/lib/siteData";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [location] = useLocation();
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setActiveMenu(null);
  }, [location]);

  // Close menus on Escape key (WCAG 2.1 SC 1.4.13)
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") {
      setActiveMenu(null);
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Close menus when clicking outside nav
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setActiveMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navLinks = [
    { label: "Services", href: "/services", hasMenu: true },
    { label: "Industries", href: "/industries", hasMenu: true },
    { label: "Pricing", href: "/pricing" },
    { label: "Case Studies", href: "/case-studies" },
    { label: "Blog", href: "/blog" },
    { label: "Locations", href: "/locations" },
    { label: "About", href: "/about" },
    { label: "SEO Training", href: "/seo-training" },
    { label: "Contact Us", href: "/contact" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-lg" : "bg-white"
      }`}
      role="banner"
    >
      {/* Top bar — navy with gold phone */}
      <div className="bg-navy border-b border-navy-dark">
        <div className="container flex items-center justify-between py-1.5">
          <p className="text-white/80 text-xs hidden sm:block">
            🏆 20+ Years of Local SEO Excellence — Nationwide Service
          </p>
          <a
            href={`tel:${SITE_PHONE}`}
            className="flex items-center gap-1.5 text-gold font-semibold text-sm hover:text-gold-light transition-colors"
            aria-label={`Call us at ${SITE_PHONE}`}
          >
            <Phone className="w-3.5 h-3.5" aria-hidden="true" />
            {SITE_PHONE}
          </a>
        </div>
      </div>

      {/* Main nav — white background */}
      <nav ref={navRef} className="container border-b border-gray-100" aria-label="Main navigation">
        <div className="flex items-center justify-between py-2">
          {/* Logo */}
          <Link href="/" className="flex-shrink-0" aria-label="Local Ranking Coach - Home">
            <img
              src={LOGO_URL}
              alt="Local Ranking Coach - Boosting Trades to the Top"
              className="h-14 md:h-16 w-auto max-w-[200px] md:max-w-[240px] object-contain"
              width="240"
              height="64"
              loading="eager"
              fetchPriority="high"
              decoding="sync"
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-0.5" role="navigation" aria-label="Main navigation">
            {navLinks.map((link) => (
              <div
                key={link.label}
                className="relative"
                onMouseEnter={() => link.hasMenu && setActiveMenu(link.label)}
                onMouseLeave={() => setActiveMenu(null)}
              >
                <Link
                  href={link.href}
                  className={`flex items-center gap-1 px-2.5 py-2 text-sm font-semibold rounded-md transition-colors ${
                    location === link.href || location.startsWith(link.href + "/")
                      ? "text-gold"
                      : "text-navy hover:text-gold"
                  }`}
                  aria-haspopup={link.hasMenu ? "true" : undefined}
                  aria-expanded={link.hasMenu ? activeMenu === link.label : undefined}
                  onKeyDown={(e) => {
                    if (link.hasMenu && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      setActiveMenu(activeMenu === link.label ? null : link.label);
                    }
                  }}
                >
                  {link.label}
                  {link.hasMenu && <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${activeMenu === link.label ? "rotate-180" : ""}`} aria-hidden="true" />}
                </Link>

                {/* Mega menus */}
                {link.hasMenu && activeMenu === link.label && (
                  <div className="absolute top-full left-0 pt-1 z-50">
                    {link.label === "Services" && (() => {
                        const aeoSubSlugs = ["aeo-agency/chatgpt-seo", "aeo-agency/claude-seo", "aeo-agency/gemini-seo", "aeo-agency/geo-seo", "aeo-agency/aio-seo"];
                        const topServices = services.filter((s) => !aeoSubSlugs.includes(s.slug));
                        const aeoSubs = services.filter((s) => aeoSubSlugs.includes(s.slug));
                        return (
                          <div className="bg-white rounded-xl shadow-2xl border border-gray-100 p-6 w-[560px] grid grid-cols-2 gap-2">
                            <div className="col-span-2 mb-2">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Our Services</p>
                            </div>
                            {topServices.map((s) => (
                              <Link
                                key={s.slug}
                                href={`/services/${s.slug}`}
                                className="flex items-start gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors group"
                              >
                                <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                                <div>
                                  <p className="text-sm font-semibold text-navy group-hover:text-gold transition-colors">{s.title}</p>
                                  <p className="text-xs text-gray-500 leading-tight mt-0.5 line-clamp-1">{s.shortDesc}</p>
                                </div>
                              </Link>
                            ))}
                            {/* AEO sub-services nested group */}
                            <div className="col-span-2 mt-2 pt-3 border-t border-gray-100">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#b8922a] inline-block" />
                                AEO Agency Sub-Services
                              </p>
                              <div className="grid grid-cols-3 gap-2">
                                {aeoSubs.map((s) => (
                                  <Link
                                    key={s.slug}
                                    href={`/services/${s.slug}`}
                                    className="flex items-start gap-2 p-2 rounded-lg hover:bg-[#fdf3d7] border border-transparent hover:border-[#e8c96a] transition-colors group"
                                  >
                                    <div className="w-1.5 h-1.5 rounded-full bg-[#b8922a] mt-2 flex-shrink-0" />
                                    <div>
                                      <p className="text-sm font-semibold text-navy group-hover:text-[#b8922a] transition-colors">{s.title}</p>
                                      <p className="text-xs text-gray-500 leading-tight mt-0.5 line-clamp-1">{s.shortDesc}</p>
                                    </div>
                                  </Link>
                                ))}
                              </div>
                            </div>
                            <div className="col-span-2 mt-2 pt-2 border-t border-gray-100">
                              <Link href="/services" className="text-xs font-semibold text-gold hover:text-gold-dark transition-colors">
                                View All Services →
                              </Link>
                            </div>
                          </div>
                        );
                      })()}
                    {link.label === "Industries" && (
                      <div className="bg-white rounded-xl shadow-2xl border border-gray-100 p-6 w-[420px] grid grid-cols-2 gap-2">
                        <div className="col-span-2 mb-2">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Industries We Serve</p>
                        </div>
                        {industries.map((ind) => (
                          <Link
                            key={ind.slug}
                            href={`/industries/${ind.slug}`}
                            className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors group"
                          >
                            <div className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                            <p className="text-sm font-semibold text-navy group-hover:text-gold transition-colors">{ind.title}</p>
                          </Link>
                        ))}
                        <div className="col-span-2 mt-2 pt-2 border-t border-gray-100">
                          <Link href="/industries" className="text-xs font-semibold text-gold hover:text-gold-dark transition-colors">
                            View All Industries →
                          </Link>
                        </div>
                      </div>
                    )}
                    </div>
                )}
              </div>
            ))}
          </div>

          {/* CTA Buttons + Mobile toggle */}
          <div className="flex items-center gap-2">
            <Link
              href="/contact"
              className="hidden md:inline-flex items-center gap-2 px-4 py-2 bg-gold text-navy font-bold text-sm rounded-md hover:bg-gold-light transition-all duration-200 active:scale-[0.97] uppercase tracking-wide"
            >
              Free Audit
            </Link>
            <a
              href="https://calendar.app.google/Pe1TK65N3x3CAeHJ9"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:inline-flex items-center gap-2 px-4 py-2 bg-navy text-white font-bold text-sm rounded-md hover:bg-navy-dark transition-all duration-200 active:scale-[0.97] uppercase tracking-wide"
            >
              Book a Call
            </a>
            <button
              className="lg:hidden p-2 text-navy hover:text-gold transition-colors"
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? "Close menu" : "Open menu"}
              aria-expanded={isOpen}
              aria-controls="mobile-menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isOpen && (
          <div
            id="mobile-menu"
            className="lg:hidden bg-white border-t border-gray-100 pb-4"
            role="navigation"
            aria-label="Mobile navigation"
          >
            <div className="flex flex-col gap-1 pt-2">
              {navLinks.map((link) => (
                <div key={link.label}>
                  <Link
                    href={link.href}
                    className="block px-4 py-2.5 text-navy hover:text-gold font-semibold text-sm transition-colors"
                  >
                    {link.label}
                  </Link>
                  {link.label === "Services" && (() => {
                      const aeoSubSlugs = ["aeo-agency/chatgpt-seo", "aeo-agency/claude-seo", "aeo-agency/gemini-seo", "aeo-agency/geo-seo", "aeo-agency/aio-seo"];
                      const topServices = services.filter((s) => !aeoSubSlugs.includes(s.slug));
                      const aeoSubs = services.filter((s) => aeoSubSlugs.includes(s.slug));
                      return (
                        <div className="pl-4 pb-2">
                          <div className="grid grid-cols-2 gap-1 mb-2">
                            {topServices.map((s) => (
                              <Link
                                key={s.slug}
                                href={`/services/${s.slug}`}
                                className="text-xs text-gray-500 hover:text-gold py-1 transition-colors"
                              >
                                {s.title}
                              </Link>
                            ))}
                          </div>
                          <div className="border-t border-gray-100 pt-2 mt-1">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">AEO Agency</p>
                            <div className="grid grid-cols-1 gap-1 pl-2">
                              {aeoSubs.map((s) => (
                                <Link
                                  key={s.slug}
                                  href={`/services/${s.slug}`}
                                  className="text-xs text-[#b8922a] font-medium hover:text-gold py-1 transition-colors"
                                >
                                  └ {s.title}
                                </Link>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  {link.label === "Industries" && (
                    <div className="pl-6 grid grid-cols-2 gap-1 pb-2">
                      {industries.map((ind) => (
                        <Link
                          key={ind.slug}
                          href={`/industries/${ind.slug}`}
                          className="text-xs text-gray-500 hover:text-gold py-1 transition-colors"
                        >
                          {ind.title}
                        </Link>
                      ))}
                    </div>
                  )}
                  </div>
              ))}
              <div className="px-4 pt-2 flex flex-col gap-2">
                <Link
                  href="/contact"
                  className="block text-center px-5 py-2.5 bg-gold text-navy font-bold text-sm rounded-md hover:bg-gold-light transition-colors uppercase tracking-wide"
                >
                  Free Audit
                </Link>
                <a
                  href="https://calendar.app.google/Pe1TK65N3x3CAeHJ9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-center px-5 py-2.5 bg-navy text-white font-bold text-sm rounded-md hover:bg-navy-dark transition-colors uppercase tracking-wide"
                >
                  Book a Call
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
